import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Users,
  Plus,
  Trash,
  PencilSimple,
  Key,
  X,
  Shield,
  User,
  GraduationCap,
  Flask,
  CheckCircle,
  XCircle,
  Clock
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const roleIcons = {
  admin: Shield,
  pi: Flask,
  researcher: User,
  student: GraduationCap
};

const roleLabels = {
  admin: 'Administrator',
  pi: 'Principal Investigator',
  researcher: 'Researcher',
  student: 'Student'
};

const roleColors = {
  admin: 'bg-red-100 text-red-400',
  pi: 'bg-purple-100 text-purple-700',
  researcher: 'bg-blue-100 text-indigo-400',
  student: 'bg-green-100 text-green-400'
};

const UserManagementPage = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showResetModal, setShowResetModal] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  
  const [newUser, setNewUser] = useState({
    email: '',
    password: '',
    name: '',
    role: 'researcher'
  });

  useEffect(() => {
    loadUsers();
    loadPendingUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const data = await api.request('GET', '/users');
      setUsers(data);
    } catch (error) {
      const msg = typeof error === 'string' ? error : 'Failed to load users';
      toast.error(msg);
      console.error('Load users error:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadPendingUsers = async () => {
    try {
      const data = await api.request('GET', '/users/pending');
      setPendingUsers(data);
    } catch { /* ignore if not admin */ }
  };

  const handleApprove = async (userId) => {
    try {
      await api.request('POST', `/users/${userId}/approve`);
      toast.success('User approved! They will receive a welcome email.');
      loadPendingUsers();
      loadUsers();
    } catch (err) {
      toast.error(typeof err === 'string' ? err : 'Failed to approve user');
    }
  };

  const handleReject = async (userId) => {
    try {
      await api.request('POST', `/users/${userId}/reject`);
      toast.success('User request declined.');
      loadPendingUsers();
      loadUsers();
    } catch (err) {
      toast.error(typeof err === 'string' ? err : 'Failed to reject user');
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    try {
      await api.request('POST', '/users', newUser);
      toast.success('User created successfully');
      setShowAddModal(false);
      setNewUser({ email: '', password: '', name: '', role: 'researcher' });
      loadUsers();
    } catch (error) {
      toast.error(typeof error === 'string' ? error : 'Failed to create user');
    }
  };

  const handleDeleteUser = async (userId, userEmail) => {
    if (window.confirm(`Are you sure you want to delete ${userEmail}?`)) {
      try {
        await api.request('DELETE', `/users/${userId}`);
        toast.success('User deleted');
        setUsers(users.filter(u => u.id !== userId));
      } catch (error) {
        toast.error(typeof error === 'string' ? error : 'Failed to delete user');
      }
    }
  };

  const handleResetPassword = async (userId) => {
    if (!newPassword || newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    try {
      await api.request('POST', `/users/${userId}/reset-password?new_password=${encodeURIComponent(newPassword)}`);
      toast.success('Password reset successfully');
      setShowResetModal(null);
      setNewPassword('');
    } catch (error) {
      toast.error('Failed to reset password');
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await api.request('PUT', `/users/${userId}`, { role: newRole });
      toast.success('Role updated');
      loadUsers();
    } catch (error) {
      toast.error('Failed to update role');
    }
  };

  const canManageUser = currentUser?.role === 'admin' || currentUser?.role === 'pi';

  if (!canManageUser) {
    return (
      <div className="text-center py-12">
        <Shield size={48} className="text-zinc-300 mx-auto mb-4" />
        <h2 className="heading-card text-slate-100 mb-2">Access Denied</h2>
        <p className="text-zinc-500">Only administrators and PIs can manage users</p>
      </div>
    );
  }

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;
  }

  return (
    <div data-testid="user-management-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="heading-display">User Management</h1>
          <p className="text-zinc-500 mt-1">Manage lab members and their access</p>
        </div>
        <Button className="btn-primary flex items-center gap-2" onClick={() => setShowAddModal(true)} data-testid="add-user-btn">
          <Plus size={16} /> Add User
        </Button>
      </div>

      {/* Pending Approvals */}
      {pendingUsers.length > 0 && (
        <div className="mb-6" data-testid="pending-approvals-section">
          <div className="flex items-center gap-2 mb-3">
            <Clock size={18} className="text-amber-500" />
            <h2 className="text-sm font-semibold" style={{ color: '#F1F5F9' }}>
              Pending Approvals ({pendingUsers.length})
            </h2>
          </div>
          <div className="space-y-2">
            {pendingUsers.map(pu => (
              <div key={pu.id} className="card-flat p-4 flex items-center justify-between border-l-4 border-amber-400" data-testid={`pending-user-${pu.id}`}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-amber-100 text-amber-300 rounded-sm flex items-center justify-center font-medium">
                    {pu.name?.charAt(0)?.toUpperCase() || 'U'}
                  </div>
                  <div>
                    <p className="font-medium text-sm" style={{ color: '#F1F5F9' }}>{pu.name}</p>
                    <p className="text-xs text-slate-400">{pu.email} — {roleLabels[pu.role] || pu.role}</p>
                    <p className="text-[10px] text-slate-300 mt-0.5">Requested {new Date(pu.created_at).toLocaleDateString()}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    className="btn-primary flex items-center gap-1"
                    onClick={() => handleApprove(pu.id)}
                    data-testid={`approve-user-${pu.id}`}
                  >
                    <CheckCircle size={14} /> Approve
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="flex items-center gap-1 text-red-400 border-red-800/40 hover:bg-red-900/20"
                    onClick={() => handleReject(pu.id)}
                    data-testid={`reject-user-${pu.id}`}
                  >
                    <XCircle size={14} /> Decline
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* User List */}
      <div className="card-flat overflow-hidden">
        <table className="data-table" data-testid="users-table">
          <thead>
            <tr>
              <th className="table-header">User</th>
              <th className="table-header">Email</th>
              <th className="table-header">Role</th>
              <th className="table-header">Status</th>
              <th className="table-header">Joined</th>
              <th className="table-header w-32">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => {
              const RoleIcon = roleIcons[user.role] || User;
              const isCurrentUser = user.id === currentUser?.id;
              
              return (
                <tr key={user.id} className="table-row" data-testid={`user-row-${user.id}`}>
                  <td className="table-cell">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-100 text-indigo-400 rounded-sm flex items-center justify-center font-medium">
                        {user.name?.charAt(0)?.toUpperCase() || 'U'}
                      </div>
                      <div>
                        <p className="font-medium text-slate-100">{user.name}</p>
                        {isCurrentUser && <span className="text-xs text-zinc-500">(You)</span>}
                      </div>
                    </div>
                  </td>
                  <td className="table-cell text-zinc-600">{user.email}</td>
                  <td className="table-cell">
                    <select
                      value={user.role}
                      onChange={(e) => handleRoleChange(user.id, e.target.value)}
                      className={`text-xs px-2 py-1 rounded-sm border-0 cursor-pointer ${roleColors[user.role] || 'bg-zinc-100 text-zinc-700'}`}
                      disabled={isCurrentUser}
                    >
                      <option value="student">Student</option>
                      <option value="researcher">Researcher</option>
                      <option value="pi">Principal Investigator</option>
                      {currentUser?.role === 'admin' && <option value="admin">Administrator</option>}
                    </select>
                  </td>
                  <td className="table-cell">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                      user.status === 'pending' ? 'bg-amber-100 text-amber-300' :
                      user.status === 'rejected' ? 'bg-red-100 text-red-400' :
                      'bg-green-100 text-green-400'
                    }`}>
                      {user.status === 'pending' ? 'Pending' : user.status === 'rejected' ? 'Declined' : 'Active'}
                    </span>
                  </td>
                  <td className="table-cell text-zinc-500">
                    {new Date(user.created_at).toLocaleDateString()}
                  </td>
                  <td className="table-cell">
                    <div className="flex gap-1">
                      <button
                        onClick={() => setShowResetModal(user)}
                        className="p-1.5 hover:bg-zinc-100 rounded-sm text-zinc-600"
                        title="Reset Password"
                        disabled={isCurrentUser}
                      >
                        <Key size={16} />
                      </button>
                      <button
                        onClick={() => handleDeleteUser(user.id, user.email)}
                        className="p-1.5 hover:bg-red-900/20 rounded-sm text-red-400"
                        title="Delete User"
                        disabled={isCurrentUser}
                      >
                        <Trash size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Add User Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-slate-800 rounded-sm p-6 w-full max-w-md shadow-lg shadow-black/30" data-testid="add-user-modal">
            <div className="flex items-center justify-between mb-4">
              <h2 className="heading-card">Add New User</h2>
              <button onClick={() => setShowAddModal(false)} className="p-1 hover:bg-zinc-100 rounded-sm">
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleAddUser} className="space-y-4">
              <div>
                <Label htmlFor="name">Full Name *</Label>
                <Input
                  id="name"
                  value={newUser.name}
                  onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                  placeholder="Dr. Jane Smith"
                  className="input-field w-full mt-1"
                  required
                />
              </div>
              <div>
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                  placeholder="jane@lab.edu"
                  className="input-field w-full mt-1"
                  required
                />
              </div>
              <div>
                <Label htmlFor="password">Password *</Label>
                <Input
                  id="password"
                  type="password"
                  value={newUser.password}
                  onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                  placeholder="Minimum 6 characters"
                  className="input-field w-full mt-1"
                  required
                  minLength={6}
                />
              </div>
              <div>
                <Label htmlFor="role">Role *</Label>
                <select
                  id="role"
                  value={newUser.role}
                  onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                  className="input-field w-full mt-1"
                >
                  <option value="student">Student</option>
                  <option value="researcher">Researcher</option>
                  <option value="pi">Principal Investigator</option>
                  {currentUser?.role === 'admin' && <option value="admin">Administrator</option>}
                </select>
              </div>
              <div className="flex gap-2 justify-end pt-4">
                <Button type="button" variant="outline" className="btn-secondary" onClick={() => setShowAddModal(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="btn-primary">
                  Add User
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {showResetModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-slate-800 rounded-sm p-6 w-full max-w-md shadow-lg shadow-black/30" data-testid="reset-password-modal">
            <div className="flex items-center justify-between mb-4">
              <h2 className="heading-card">Reset Password</h2>
              <button onClick={() => { setShowResetModal(null); setNewPassword(''); }} className="p-1 hover:bg-zinc-100 rounded-sm">
                <X size={20} />
              </button>
            </div>
            <p className="text-sm text-zinc-500 mb-4">
              Reset password for <strong>{showResetModal.name}</strong> ({showResetModal.email})
            </p>
            <div className="space-y-4">
              <div>
                <Label htmlFor="newPassword">New Password *</Label>
                <Input
                  id="newPassword"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Minimum 6 characters"
                  className="input-field w-full mt-1"
                  minLength={6}
                />
              </div>
              <div className="flex gap-2 justify-end pt-4">
                <Button type="button" variant="outline" className="btn-secondary" onClick={() => { setShowResetModal(null); setNewPassword(''); }}>
                  Cancel
                </Button>
                <Button onClick={() => handleResetPassword(showResetModal.id)} className="btn-primary">
                  Reset Password
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagementPage;
